/**
 * Contrato FE — Regressão NBR Enterprise (PRE-B4 / RQ-030B).
 * Infraestrutura completa · sem chamada OLS · sem dados fictícios.
 *
 * Políticas locked: R² alerta+confirm; oferta ⟂ elasticidade.
 * Spec canónica: docs/valuation/AVM_VALUATION_ENTERPRISE_B4_REGRESSION_CONTRACT_V1.md
 */

import {
  ELASTICITY_DEFAULT_RATE,
  OFFER_FACTOR_OPTIONS,
  R2_OFFICIAL_THRESHOLD,
  R2_POLICY
} from "./valuationAutomaticContract_v1";

export const REGRESSION_STEP_STATUS = "b4_runtime";

export const REGRESSION_MODEL_VERSION = "challenger_v1_core";

/** Paths B4 runtime (RQ-033B). */
export const REGRESSION_API_PATHS = Object.freeze({
  validate: "/api/v1/valuation-automatic/regression/validate",
  run: "/api/v1/valuation-automatic/regression/run",
  get: "/api/v1/valuation-automatic/regression",
  confirm: "/api/v1/valuation-automatic/regression",
  predict: "/api/v1/valuation-automatic/regression/predict",
  diagnostics: "/api/v1/valuation-automatic/regression/diagnostics"
});

export const REGRESSION_UI_REFS = Object.freeze({
  offerFactorOptions: OFFER_FACTOR_OPTIONS,
  elasticityDefaultRate: ELASTICITY_DEFAULT_RATE,
  r2OfficialThreshold: R2_OFFICIAL_THRESHOLD,
  r2Policy: R2_POLICY
});

/** Secções de UI do Step 6 (slots vazios). */
export const REGRESSION_UI_SECTIONS = Object.freeze([
  "comparaveis_selecionados",
  "resumo_amostra",
  "variaveis_utilizadas",
  "variaveis_descartadas",
  "cobertura",
  "avisos",
  "diagnosticos",
  "fator_oferta",
  "elasticidade",
  "status_amostra",
  "qualidade",
  "indicadores",
  "progresso",
  "auditoria_futura"
]);

/** Campos de resultado estatístico — todos null até B4 runtime. */
export function createEmptyRegressionDiagnostics() {
  return {
    r2: null,
    r2_adjusted: null,
    f_statistic: null,
    f_pvalue: null,
    anova: null,
    t_statistics: [],
    significance: null,
    residuals: null,
    normality: { test: "shapiro", p_value: null },
    homoscedasticity: { test: "breusch_pagan", p_value: null },
    autocorrelation: { test: "durbin_watson", value: null },
    vif: [],
    ic: { level: null, low: null, high: null },
    ip: { level: null, low: null, high: null },
    elasticidades_estatisticas: [],
    nbr_status: null,
    fundamentacao: null,
    alerts: []
  };
}

export function createEmptyRegressionResult() {
  return {
    vu_bruto: null,
    vu_ajustado: null,
    valor_total: null,
    valor_min: null,
    valor_central: null,
    valor_max: null,
    offer_factor: { id: null, rate: null, applied: false, trail: [] },
    elasticity: { applied: false, rate: null, trail: null },
    diagnostics: createEmptyRegressionDiagnostics(),
    variables_used: [],
    variables_dropped: [],
    comparables_used: [],
    comparables_excluded: [],
    model_version: REGRESSION_MODEL_VERSION,
    selection_version: null,
    operator_confirmations: {},
    audit_trail: null
  };
}

/** Contratos de relatórios (independentes; vazios). */
export function createEmptyReportContracts() {
  return {
    relatorio_estatistico: {
      status: "not_generated",
      payload: null
    },
    relatorio_modelo: {
      status: "not_generated",
      payload: null
    },
    laudo_individual: {
      status: "not_generated",
      payload: null
    }
  };
}

/**
 * Estado vazio do passo Regressão — sem dados inventados.
 */
export function createEmptyRegressionState() {
  return {
    status: REGRESSION_STEP_STATUS,
    loading: false,
    error: null,
    readyToRun: false,
    gate: { ok: false, errors: [], warnings: [] },
    modelVersion: REGRESSION_MODEL_VERSION,
    selectionVersion: null,
    enrichmentVersion: null,
    offerFactorId: "offer_0",
    applyElasticity: false,
    elasticityRate: 0,
    weakGeocodeConfirmed: false,
    confirmOfficialDespiteR2: false,
    sampleSummary: {
      selectedCount: 0,
      excludedCount: 0,
      coverage: null,
      qualityLabel: null
    },
    ui: {
      sections: REGRESSION_UI_SECTIONS,
      message: "Aguardando execução da regressão."
    },
    result: createEmptyRegressionResult(),
    reports: createEmptyReportContracts(),
    alerts: [],
    audit: null,
    progress: {
      phase: "idle",
      percent: 0,
      detail: null
    }
  };
}

export const REGRESSION_INPUT_REQUIRED_KEYS = Object.freeze([
  "subject.areaPrivativa",
  "subject.tipologia",
  "subject.latitude",
  "subject.longitude",
  "selection.selected_ids",
  "offerFactorId"
]);

/** Adapters documentados (stubs). */
export const REGRESSION_ADAPTER_TARGETS = Object.freeze([
  "sisaval",
  "stats_engine",
  "nbr_validator",
  "model_governance"
]);
